/**
 * Booking hooks — TanStack Query v5 for booking CRUD.
 *
 * Query key factory exported for cross-hook invalidation (use-cancellation
 * and use-hosting-dashboard invalidate through it).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { bookingService } from '../services/booking.service'
import { respondToPaidRental, type HostDecision } from '../services/host-approval.service'
import { useAuth } from './use-auth'
import type { CreateBookingInput, BookingListFilter, BookingRole } from '../types/booking.types'

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const bookingKeys = {
  all: ['bookings'] as const,
  detail: (bookingId: string) => [...bookingKeys.all, 'detail', bookingId] as const,
  list: (userId: string, role: BookingRole, filter: BookingListFilter) =>
    [...bookingKeys.all, 'list', userId, role, filter] as const,
  unavailableRanges: (vehicleId: string, fromDate: string, toDate: string) =>
    [...bookingKeys.all, 'unavailable-ranges', vehicleId, fromDate, toDate] as const,
}

// ─── Vehicle Calendar Availability ──────────────────────────────────────────

export function useVehicleUnavailableRanges(
  vehicleId: string | undefined,
  fromDate: string,
  toDate: string,
) {
  const query = useQuery({
    queryKey: bookingKeys.unavailableRanges(vehicleId ?? '', fromDate, toDate),
    queryFn: () =>
      bookingService.getVehicleUnavailableRanges({
        vehicleId: vehicleId!,
        fromDate,
        toDate,
      }),
    enabled: !!vehicleId,
    retry: false,
    staleTime: 30 * 1000,
  })

  return {
    ranges: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

// ─── Single Booking Detail ──────────────────────────────────────────────────

export function useBooking(bookingId: string | undefined) {
  return useQuery({
    queryKey: bookingKeys.detail(bookingId ?? ''),
    queryFn: () => bookingService.getBookingDetail(bookingId!),
    enabled: !!bookingId,
    retry: false,
    staleTime: 2 * 60 * 1000, // 2 min — booking status can change
  })
}

export function useRespondToPaidRental() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ rentalId, decision }: { rentalId: string; decision: HostDecision }) =>
      respondToPaidRental(rentalId, decision),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all })
      void queryClient.invalidateQueries({ queryKey: ['hosting'] })
    },
  })
  return { respond: mutation.mutateAsync, isPending: mutation.isPending }
}

// ─── User Bookings List ─────────────────────────────────────────────────────

export function useUserBookings(role: BookingRole = 'guest', filter: BookingListFilter = 'all') {
  const { user } = useAuth()
  const userId = user?.id ?? ''

  const query = useQuery({
    queryKey: bookingKeys.list(userId, role, filter),
    queryFn: () => bookingService.getUserBookings(userId, role, filter),
    enabled: !!userId,
    // 15s — short enough that a brand-new booking shows up in Trips after
    // the user navigates back from checkout, without hammering the DB.
    staleTime: 15 * 1000,
  })

  return {
    bookings: query.data ?? [],
    isLoading: query.isLoading,
    isRefetching: query.isRefetching,
    error: query.error,
    refetch: query.refetch,
  }
}

// ─── Create Booking ─────────────────────────────────────────────────────────

export function useCreateBooking() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const mutation = useMutation({
    mutationFn: (input: CreateBookingInput) => bookingService.createBooking(user!.id, input),
    onSuccess: (booking) => {
      // Populate detail cache
      queryClient.setQueryData(bookingKeys.detail(booking.id), booking)
      // Invalidate all booking lists
      queryClient.invalidateQueries({ queryKey: bookingKeys.all })
      queryClient.invalidateQueries({
        queryKey: [...bookingKeys.all, 'unavailable-ranges', booking.vehicleId],
      })
    },
  })

  return {
    createBooking: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  }
}

export function useReleaseUnpaidHold() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (rentalId: string) => bookingService.releaseUnpaidHold(rentalId),
    onSuccess: (released) => {
      if (!released) return
      queryClient.invalidateQueries({ queryKey: bookingKeys.all })
    },
  })

  return {
    releaseUnpaidHold: mutation.mutateAsync,
  }
}
